import { useMemo } from "react";
import { useGetList, useListContext } from "ra-core";
import { Link } from "react-router";
import {
  AlertTriangle,
  ChevronRight,
  Mail,
  MessageCircle,
  Phone,
  RotateCw,
} from "lucide-react";

import { cn } from "@/lib/utils";

import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import { MemberAvatar } from "../shared/MemberAvatar";
import { Sparkline } from "../shared/Sparkline";
import { sizes } from "./sizes";

// Sprint 8 — Table dense /companies conforme E_D1 (ent-direction1.jsx +
// ent-shared.jsx CompaniesTable).
// 9 colonnes : checkbox / Entreprise / Population / Température /
// Prochaine action / Contacts liés / Activité 30j / Resp / chevron.
//
// Sprint 10.x — Data join : useListContext (companies) + useGetList(
// companies_overview) pour les colonnes agrégat ET les nouveaux soft KPI
// filters (brulant, chaud, tiede, froid_mort, orphan, overdue, paying,
// has_pipeline, recent_7d).

type HeatTier = "brulant" | "chaud" | "tiede" | "froid" | "mort";

type Summary = {
  id: number | string;
  company_id: number;
  max_temperature: "chaud" | "a_chauffer" | "froid" | "perdu" | null;
  max_heat_tier: HeatTier | null;
  next_action_type:
    | "a_appeler"
    | "a_mailer"
    | "a_engager"
    | "a_relancer"
    | null;
  next_action_date: string | null;
  contacts_actifs: number;
  contacts_total: number;
  nb_deals: number;
  nb_open_deals: number;
  nb_active_plans: number;
  has_overdue_tasks: boolean;
  overdue_tasks_count: number;
  is_paying_loose: boolean;
  is_paying_strict: boolean;
  pipeline_open_eur: number;
  mrr_monthly_eur: number;
  age_days: number;
};

const TEMP_CONFIG: Record<
  Exclude<Summary["max_temperature"], null>,
  { label: string; dot: string; text: string; bg: string }
> = {
  chaud: {
    label: "Chaud",
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-300",
    bg: "bg-red-50 dark:bg-red-950/30",
  },
  a_chauffer: {
    label: "À chauffer",
    dot: "bg-orange-500",
    text: "text-orange-700 dark:text-orange-300",
    bg: "bg-orange-50 dark:bg-orange-950/30",
  },
  froid: {
    label: "Froid",
    dot: "bg-blue-500",
    text: "text-blue-700 dark:text-blue-300",
    bg: "bg-blue-50 dark:bg-blue-950/30",
  },
  perdu: {
    label: "Perdu",
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
    bg: "bg-muted",
  },
};

const ACTION_CONFIG: Record<
  Exclude<Summary["next_action_type"], null>,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  a_relancer: { label: "À relancer", icon: RotateCw },
  a_appeler: { label: "À appeler", icon: Phone },
  a_mailer: { label: "Envoyer démo", icon: Mail },
  a_engager: { label: "À engager", icon: MessageCircle },
};

function formatWhen(dateIso: string | null): {
  text: string;
  urgent: boolean;
} {
  if (!dateIso) return { text: "—", urgent: false };
  try {
    const d = new Date(dateIso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(d);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.round(
      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (diffDays < 0) return { text: "En retard", urgent: true };
    if (diffDays === 0) return { text: "Aujourd'hui", urgent: true };
    if (diffDays === 1) return { text: "Demain", urgent: true };
    if (diffDays <= 7) return { text: `Dans ${diffDays}j`, urgent: false };
    if (diffDays <= 14) return { text: "Cette sem.", urgent: false };
    return {
      text: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }),
      urgent: false,
    };
  } catch {
    return { text: "—", urgent: false };
  }
}

function sizeLabel(size: number | null | undefined): string {
  if (size == null) return "—";
  const found = sizes.find((s) => s.id === size);
  return found ? `${found.name} empl.` : `${size} empl.`;
}

interface CompanyTableProps {
  /** Filtres "soft" appliqués côté frontend. */
  softFilters?: {
    population?: string;
    /** Filtre legacy par max_temperature manuelle. */
    temperature?: string;
    /** Sprint 10.x — Filtre KPI (Signal + Valeur). */
    kpi?: string;
  };
}

export const CompanyTable = ({ softFilters }: CompanyTableProps) => {
  const { data: companies, isPending } = useListContext<Company>();

  // Charger les summaries (agg heat + temperature + action + counts + business)
  // en 1 query. Sprint 10.x : on consomme companies_overview (superset de
  // companies_temperature_summary + heat IA + payment + business).
  const { data: summaries } = useGetList<Summary>("companies_overview", {
    pagination: { page: 1, perPage: 500 },
  });

  const summariesById = useMemo(() => {
    const map = new Map<number, Summary>();
    (summaries ?? []).forEach((s) => map.set(Number(s.company_id), s));
    return map;
  }, [summaries]);

  // Sprint 10.x — Une company est "plateforme" si elle a >= 1 contact
  // population='plateforme'. Sinon "prospection". Toujours chargé (sert
  // au filtre ET au rendu de la colonne Population dans la table).
  const { data: plateformeContacts } = useGetList<{ company_id: number }>(
    "contacts_summary",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "company_id", order: "ASC" },
      filter: { population: "plateforme" },
    },
  );
  const plateformeCompanyIds = useMemo(
    () =>
      new Set(
        (plateformeContacts ?? [])
          .map((c) => c.company_id)
          .filter((id): id is number => id != null),
      ),
    [plateformeContacts],
  );

  // Appliquer les filtres "soft" (population / temperature / kpi côté front)
  const rows = useMemo(() => {
    if (!companies) return [];
    return companies.filter((c) => {
      const sum = summariesById.get(Number(c.id));
      if (softFilters?.temperature) {
        if (sum?.max_temperature !== softFilters.temperature) return false;
      }
      if (softFilters?.population === "plateforme") {
        if (!plateformeCompanyIds.has(Number(c.id))) return false;
      } else if (softFilters?.population === "prospection") {
        if (plateformeCompanyIds.has(Number(c.id))) return false;
      }
      // Sprint 10.x — soft KPI filter (Signal + Valeur). Si le row n'a
      // pas de summary (cas dégradé), on l'écarte sauf pour "orphan"
      // (orphelin = explicitement contacts_total === 0).
      if (softFilters?.kpi) {
        const kpi = softFilters.kpi;
        if (kpi === "brulant") {
          if (sum?.max_heat_tier !== "brulant") return false;
        } else if (kpi === "chaud") {
          if (sum?.max_heat_tier !== "chaud") return false;
        } else if (kpi === "tiede") {
          if (sum?.max_heat_tier !== "tiede") return false;
        } else if (kpi === "froid_mort") {
          if (sum?.max_heat_tier !== "froid" && sum?.max_heat_tier !== "mort")
            return false;
        } else if (kpi === "orphan") {
          if ((sum?.contacts_total ?? 0) !== 0) return false;
        } else if (kpi === "overdue") {
          if (sum?.has_overdue_tasks !== true) return false;
        } else if (kpi === "paying") {
          if (sum?.is_paying_loose !== true) return false;
        } else if (kpi === "has_pipeline") {
          if ((sum?.pipeline_open_eur ?? 0) <= 0) return false;
        } else if (kpi === "recent_7d") {
          if ((sum?.age_days ?? 999) > 7) return false;
        }
      }
      return true;
    });
  }, [companies, summariesById, softFilters, plateformeCompanyIds]);

  if (isPending) {
    return (
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {[1, 2, 3, 4].map((n) => (
          <div
            key={n}
            className="h-14 border-b border-border last:border-0 px-4 flex items-center gap-3"
          >
            <div className="size-8 rounded-md bg-muted animate-pulse" />
            <div className="h-3 w-40 rounded bg-muted animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground text-[13px]">
        Aucune entreprise ne correspond aux filtres.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="w-10 px-3 py-2.5 text-left">
                <span className="sr-only">Sélection</span>
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                Entreprise
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                Population
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                Température
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                Prochaine action
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                Contacts liés
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                Activité 30j
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                Resp.
              </th>
              <th className="w-10 px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <CompanyTableRow
                key={c.id}
                company={c}
                summary={summariesById.get(Number(c.id))}
                isPlateforme={plateformeCompanyIds.has(Number(c.id))}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function CompanyTableRow({
  company,
  summary,
  isPlateforme,
}: {
  company: Company;
  summary: Summary | undefined;
  isPlateforme: boolean;
}) {
  const temp = summary?.max_temperature
    ? TEMP_CONFIG[summary.max_temperature]
    : null;
  const action = summary?.next_action_type
    ? ACTION_CONFIG[summary.next_action_type]
    : null;
  const when = formatWhen(summary?.next_action_date ?? null);
  const contactsTotal = summary?.contacts_total ?? 0;
  const isOrphan = contactsTotal === 0;

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <td className="px-3 py-2.5">
        {/* Bulk checkbox — placeholder, à câbler avec useListContext.onToggleItem */}
        <input
          type="checkbox"
          className="size-3.5 rounded border-border accent-violet-600"
          aria-label={`Sélectionner ${company.name}`}
        />
      </td>

      {/* Entreprise */}
      <td className="px-3 py-2.5">
        <Link
          to={`/companies/${company.id}/show`}
          className="flex items-center gap-2.5 min-w-0 group"
        >
          <CompanyAvatar
            record={company}
            width={32}
            height={32}
            className="shrink-0"
          />
          <div className="min-w-0">
            <div className="font-medium text-foreground truncate group-hover:underline">
              {company.name}
            </div>
            <div className="text-[11.5px] text-muted-foreground truncate">
              {company.sector ?? "—"}
              {company.size != null && (
                <>
                  <span className="mx-1.5 text-muted-foreground/50">·</span>
                  {sizeLabel(company.size)}
                </>
              )}
            </div>
          </div>
        </Link>
      </td>

      {/* Population — Plateforme si la company a >= 1 contact plateforme */}
      <td className="px-3 py-2.5">
        {isPlateforme ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-fabrik-green-soft text-fabrik-accent-text px-2 py-0.5 text-[11.5px] font-medium">
            <span className="size-1.5 rounded-full bg-fabrik-green" />
            Plateforme
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-fabrik-orange-soft text-fabrik-orange px-2 py-0.5 text-[11.5px] font-medium">
            <span className="size-1.5 rounded-full bg-fabrik-orange" />
            Prospection
          </span>
        )}
      </td>

      {/* Température */}
      <td className="px-3 py-2.5">
        {temp ? (
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11.5px] font-medium",
              temp.bg,
              temp.text,
            )}
          >
            <span className={cn("size-1.5 rounded-full", temp.dot)} />
            {temp.label}
          </span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground/70">—</span>
        )}
      </td>

      {/* Prochaine action */}
      <td className="px-3 py-2.5">
        {action ? (
          <div
            className={cn(
              "inline-flex items-center gap-1.5 text-[12px]",
              when.urgent && "text-red-600 dark:text-red-300 font-medium",
            )}
          >
            <action.icon className="size-3 shrink-0" />
            <span>{action.label}</span>
            <span className="text-muted-foreground">·</span>
            <span>{when.text}</span>
          </div>
        ) : (
          <span className="text-[11.5px] text-muted-foreground/70">
            Aucune action
          </span>
        )}
      </td>

      {/* Contacts liés */}
      <td className="px-3 py-2.5">
        {isOrphan ? (
          <span className="inline-flex items-center gap-1 text-[11.5px] text-violet-600 dark:text-violet-300 font-medium">
            <AlertTriangle className="size-3" />
            Orphelin
          </span>
        ) : (
          <span className="text-[12px] text-foreground tabular-nums">
            {contactsTotal} contact{contactsTotal > 1 ? "s" : ""}
          </span>
        )}
      </td>

      {/* Activité 30j */}
      <td className="px-3 py-2.5">
        <Sparkline data={null} />
      </td>

      {/* Resp. */}
      <td className="px-3 py-2.5">
        {company.sales_id != null ? (
          <MemberAvatar salesId={company.sales_id} size={26} />
        ) : (
          <span className="text-[11.5px] text-muted-foreground/70">—</span>
        )}
      </td>

      {/* Chevron */}
      <td className="px-3 py-2.5 text-right">
        <Link
          to={`/companies/${company.id}/show`}
          className="inline-flex items-center justify-center size-7 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
          aria-label={`Ouvrir ${company.name}`}
        >
          <ChevronRight className="size-4" />
        </Link>
      </td>
    </tr>
  );
}
